/**
 * Per-term highlight colors for multi-row keyword queries (AND/OR rows).
 * Each query row gets a distinct color by its index; the same color is used
 * for that term in the results, compare, and export/snapshot views so a user
 * can tell which row a highlight came from. Palette cycles after it runs out.
 */
export interface ColoredTerm {
  term: string;
  color: string; // mark background (light, readable with dark text)
}

// Light, distinct, accessible-on-dark-text backgrounds. Index 0 = the classic
// yellow so single-term searches look unchanged.
export const HIGHLIGHT_PALETTE: readonly string[] = [
  '#fde047', // yellow
  '#93c5fd', // blue
  '#86efac', // green
  '#f9a8d4', // pink
  '#fdba74', // orange
  '#c4b5fd', // violet
  '#5eead4', // teal
  '#fca5a5', // red
];

/** Manual (user-selected) highlights on the Compare page + in its exports.
 *  Deliberately NOT in HIGHLIGHT_PALETTE so it never collides with a query row. */
export const USER_HIGHLIGHT_COLOR = '#bef264'; // lime

export function colorForIndex(i: number): string {
  return HIGHLIGHT_PALETTE[i % HIGHLIGHT_PALETTE.length]!;
}

/** Ordered query terms → [{term, color}], de-duped (case-insensitive), blanks dropped. */
export function buildColoredTerms(terms: (string | null | undefined)[]): ColoredTerm[] {
  const seen = new Set<string>();
  const out: ColoredTerm[] = [];
  for (const raw of terms) {
    const t = (raw ?? '').trim();
    if (!t) continue;
    const key = t.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({ term: t, color: colorForIndex(out.length) });
  }
  return out;
}
